import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import { 
  Target, 
  Users, 
  BookOpen, 
  Award,
  Mail,
  Phone,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Shield,
  Zap,
  BarChart3,
  GraduationCap,
  Building2
} from 'lucide-react';

const AboutPage = () => {
  // Stats
  const stats = [
    { number: "1,500+", label: "Active Aspirants", icon: Users },
    { number: "5,000+", label: "Tests Completed", icon: BookOpen },
    { number: "10,000+", label: "Answers Evaluated", icon: CheckCircle }
  ];

  // Features
  const features = [
    {
      icon: BarChart3,
      title: "Smart Analytics",
      description: "Track your preparation with detailed performance insights and selection probability scoring"
    },
    {
      icon: Zap,
      title: "Daily Intelligence",
      description: "Curated current affairs with Prelims and Mains relevance analysis, updated every morning"
    },
    {
      icon: GraduationCap,
      title: "Answer Evaluation",
      description: "Get your handwritten answers evaluated with detailed feedback and improvement suggestions"
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Monitor your consistency, accuracy, and improvement trajectory across all subjects"
    },
    {
      icon: Shield,
      title: "Exam-Focused Content",
      description: "Every feature designed specifically for UPSC Civil Services preparation"
    },
    {
      icon: Target,
      title: "Personalized Guidance",
      description: "Get recommendations based on your performance patterns and weak areas"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      <SEO
        title="About CLARIAS | Examiner-Trained UPSC Preparation Platform"
        description="CLARIAS is a UPSC-trained preparation platform with rubric-graded Mains evaluation, daily editorial-tagged Current Affairs, Prelims practice and Strategy Analytics — built for serious civil services aspirants."
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Target className="w-4 h-4" />
            UPSC Preparation Platform
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-900 mb-6 leading-tight">
            Clarity over Chaos
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            CLARIAS is a comprehensive preparation platform built exclusively for UPSC Civil Services aspirants. 
            We combine structured learning with intelligent analytics to help you prepare smarter, not harder.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Start Preparing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mb-4">Our Vision</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-orange-500 mx-auto rounded-full"></div>
          </div>
          
          <Card className="border-0 shadow-xl bg-gradient-to-br from-indigo-600 to-indigo-800 text-white">
            <CardContent className="p-8 sm:p-12">
              <p className="text-xl sm:text-2xl leading-relaxed text-center font-light">
                "To democratize quality UPSC preparation by providing every aspirant — regardless of their location, 
                background, or resources — access to structured guidance, intelligent feedback, and the tools 
                they need to compete at the highest level."
              </p>
              <div className="mt-8 text-center">
                <p className="text-indigo-200 text-sm uppercase tracking-wider">
                  Making civil services preparation accessible to all
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <stat.icon className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                  <div className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{stat.number}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mb-4">What We Offer</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Every feature is designed with one goal — to help you clear UPSC
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* For Institutes Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-900 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Building2 className="w-12 h-12 text-orange-400 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4">For Coaching Institutes</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Partner with CLARIAS to give your students access to premium preparation tools. 
            White-label solutions available with custom branding.
          </p>
          <a href="mailto:support@clarias.in">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-lg px-8">
              Contact for Partnership
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-slate-600">
              Have questions? We'd love to hear from you.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card className="border border-slate-200 hover:border-indigo-300 transition-colors">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Email Us</h3>
                <a 
                  href="mailto:support@clarias.in" 
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  support@clarias.in
                </a>
              </CardContent>
            </Card>
            
            <Card className="border border-slate-200 hover:border-indigo-300 transition-colors">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Call Us</h3>
                <a 
                  href="tel:+919154900228" 
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  +91 91549 00228
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;
